/* eslint-disable jsx-a11y/alt-text */
import { GetStaticPropsContext, GetStaticPropsResult } from 'next';
import { useRouter } from 'next/router';
import { z } from 'zod';

import { BlobProvider, Document, Font, Page, View } from '@react-pdf/renderer';

import { PageCategory } from '~/features/analytics/constants';
import { CatalogCardItems } from '~/features/catalog/models/catalog-card-item';
import { publicEnv } from '~/features/environment/public';
import { logger } from '~/features/logging/logger';
import { PageHead } from '~/features/page/components/page-head';
import { getPage } from '~/features/page/controllers/get-page';
import type { Page as PageType } from '~/features/page/models/page';
import { getProgram } from '~/features/program/controllers/get-program';
import { getProgramContent } from '~/features/program/controllers/get-program-content';
import { getProgramMetadata } from '~/features/program/controllers/get-program-metadata';
import { getProgramReviewSummary } from '~/features/program/controllers/get-program-review-summary';
import { getProgramVersion } from '~/features/program/controllers/get-program-version';
import { getRelatedPrograms } from '~/features/program/controllers/get-related-programs';
import { useLocalizedProgram } from '~/features/program/hooks/use-localized-program';
import { Program } from '~/features/program/models/program';
import { ProgramContent } from '~/features/program/models/program-content';
import { ProgramMetadata } from '~/features/program/models/program-metadata';
import { ProgramReviewSummary } from '~/features/program/models/program-review-summary';
import { isNanodegreeProgram } from '~/features/program/utils/is-program-type';
import { translateSemanticType } from '~/features/program/utils/translate-semantic-type';
import { DownloadSyllabusBlobContainer } from '~/features/syllabus/components/download-syllabus-blob-container';
import { SyllabusCourseOverview } from '~/features/syllabus/components/syllabus-course-overview';
import { SyllabusPrerequisites } from '~/features/syllabus/components/syllabus-course-prerequisites';
import { SyllabusCourses } from '~/features/syllabus/components/syllabus-courses';
import { SyllabusCover } from '~/features/syllabus/components/syllabus-cover';
import { SyllabusFooter, SyllabusHeader } from '~/features/syllabus/components/syllabus-header-footer';
import { SyllabusInstructors } from '~/features/syllabus/components/syllabus-instructors';
import { SyllabusSkills } from '~/features/syllabus/components/syllabus-skills';
import { styles as sharedStyles } from '~/features/syllabus/components/syllabus-styles';
import { SyllabusWhyUdacity } from '~/features/syllabus/components/syllabus-why-udacity';
import {
  SYLLABUS_AUTO_DOWNLOAD_QUERY_KEY,
  SYLLABUS_AUTO_DOWNLOAD_QUERY_VALUE,
} from '~/features/syllabus/utils/syllabus-download-query';
import {
  buildSyllabusPdfDownloadBaseName,
  buildSyllabusPdfKeywords,
} from '~/features/syllabus/utils/syllabus-pdf-download';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';

Font.register({
  family: 'PolySans',
  fonts: [
    {
      src: '/fonts/PolySans-Bold.woff2',
      fontWeight: 700,
      fontStyle: 'normal',
    },
    {
      src: '/fonts/PolySans-BoldItalic.woff2',
      fontWeight: 700,
      fontStyle: 'italic',
    },
    {
      src: '/fonts/PolySans-Median.woff2',
      fontWeight: 500,
      fontStyle: 'normal',
    },
    {
      src: '/fonts/PolySans-MedianItalic.woff2',
      fontWeight: 500,
      fontStyle: 'italic',
    },
    {
      src: '/fonts/PolySans-Neutral.woff2',
      fontWeight: 400,
      fontStyle: 'normal',
    },
    {
      src: '/fonts/PolySans-NeutralItalic.woff2',
      fontWeight: 400,
      fontStyle: 'italic',
    },
    {
      src: '/fonts/PolySans-Slim.woff2',
      fontWeight: 300,
      fontStyle: 'normal',
    },
    {
      src: '/fonts/PolySans-SlimItalic.woff2',
      fontWeight: 300,
      fontStyle: 'italic',
    },
  ],
});

Font.register({
  family: 'Open Sans',
  fonts: [
    {
      src: '/fonts/OpenSans-Regular.ttf',
      fontWeight: 400,
    },
    {
      src: '/fonts/OpenSans-Medium.ttf',
      fontWeight: 500,
    },
    {
      src: '/fonts/OpenSans-SemiBold.ttf',
      fontWeight: 600,
    },
    {
      src: '/fonts/OpenSans-Bold.ttf',
      fontWeight: 700,
    },
  ],
});

Font.register({
  family: 'Inter',
  fonts: [
    {
      src: '/fonts/Inter-SemiBold.ttf',
      fontWeight: 600,
    },
  ],
});

export type SyllabusPageProps = {
  programContentVersion: string | null;
  page: PageType;
  program: Program;
  programContent: ProgramContent;
  programMetadata: ProgramMetadata;
  relatedPrograms: CatalogCardItems;
  reviewSummary: ProgramReviewSummary;
};

export default function SyllabusPage({
  programContentVersion,
  program,
  programMetadata,
  programContent,
  reviewSummary,
  page,
}: SyllabusPageProps) {
  const router = useRouter();
  const isClient = useClientRender();
  const { t } = useI18n();
  const programPartKeys = isNanodegreeProgram(program) ? program.parts.map((part) => part.key) : [];
  const { program: localizedProgram, programMetadata: localizedProgramMetadata } = useLocalizedProgram({
    initialLanguage: 'en-US',
    initialProgram: program,
    initialProgramMetadata: programMetadata,
    key: program.key,
    programKeys: [program.key, ...programPartKeys],
    version: programContentVersion,
  });
  const renderedProgram = localizedProgram ?? program;
  const renderedProgramMetadata = localizedProgramMetadata ?? programMetadata;
  const autoDownload =
    router.isReady && router.query[SYLLABUS_AUTO_DOWNLOAD_QUERY_KEY] === SYLLABUS_AUTO_DOWNLOAD_QUERY_VALUE;
  const translatedSemanticType = translateSemanticType(t, renderedProgram.semanticType);
  const schools = renderedProgramMetadata.rootProgram?.schools || [];
  // Use the primary school or fallback to the first school if there is no primary school
  let schoolName = schools.find((school) => school.isPrimary)?.name;
  if (!schoolName) {
    schoolName = schools[0]?.name;
  }
  const syllabusPdfKeywords = buildSyllabusPdfKeywords(programContentVersion);
  const syllabus = (
    <Document keywords={syllabusPdfKeywords} title={renderedProgram.title}>
      <SyllabusCover school={schoolName} title={renderedProgram.title} />
      <Page size='LETTER' style={sharedStyles.page} wrap>
        <SyllabusHeader title={renderedProgram.title} />
        <View style={sharedStyles.bodyContainer}>
          <SyllabusCourseOverview
            displayDuration={renderedProgram.displayRawDuration || ''}
            level={renderedProgramMetadata.rootProgram?.difficultyLevel?.name || ''}
            overview={renderedProgram.summary || ''}
            semanticType={translatedSemanticType || ''}
            title={renderedProgram.title || ''}
            reviewSummary={
              reviewSummary
                ? {
                    starsAverage: reviewSummary.starsAverage,
                    numberOfReviews: reviewSummary.numberOfReviews,
                  }
                : undefined
            }
          />
          <SyllabusPrerequisites prerequisites={renderedProgramMetadata.allPrerequisites || []} />
          <SyllabusSkills program={renderedProgram} programMetadata={renderedProgramMetadata} />
        </View>
        <SyllabusFooter />
      </Page>
      <Page size='LETTER' style={sharedStyles.page} wrap>
        <SyllabusHeader title={renderedProgram.title} />
        <View style={sharedStyles.bodyContainer}>
          <SyllabusCourses program={renderedProgram} />
        </View>
        <SyllabusFooter />
      </Page>
      {!!programContent?.instructors?.length && (
        <Page size='LETTER' style={sharedStyles.page} wrap>
          <SyllabusHeader title={renderedProgram.title} />
          <View style={sharedStyles.bodyContainer}>
            <SyllabusInstructors instructors={programContent.instructors} />
          </View>
          <SyllabusFooter />
        </Page>
      )}
      <Page size='LETTER' style={sharedStyles.page} wrap>
        <SyllabusHeader title={renderedProgram.title} />
        <View style={sharedStyles.bodyContainer}>
          <SyllabusWhyUdacity />
        </View>
        <SyllabusFooter />
      </Page>
    </Document>
  );

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.Syllabus} programContent={programContent} />
      {isClient && (
        <BlobProvider document={syllabus}>
          {(props) => (
            <DownloadSyllabusBlobContainer
              {...props}
              autoDownload={autoDownload}
              downloadBaseName={buildSyllabusPdfDownloadBaseName(program.key, programContentVersion)}
            />
          )}
        </BlobProvider>
      )}
    </>
  );
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export async function getStaticProps({
  params,
}: GetStaticPropsContext<{ key: string }>): Promise<GetStaticPropsResult<SyllabusPageProps>> {
  const programKeySchema = z.string().safeParse(params?.key);

  if (!programKeySchema.success) {
    return {
      notFound: true,
    };
  }

  const programKey = programKeySchema.data;

  // Unpublished programs will return null
  const latestProgramVersion = await getProgramVersion(programKey);

  // Don't allow access to unpublished programs in production
  if (!latestProgramVersion && publicEnv.NEXT_PUBLIC_ENVIRONMENT_NAME === 'production') {
    return {
      notFound: true,
    };
  }

  try {
    const program = await getProgram({ key: programKey, version: latestProgramVersion });

    const programPartKeys = isNanodegreeProgram(program) ? program.parts.map((part) => part.key) : [];

    const [page, programMetadata, programContent, reviewSummary] = await Promise.all([
      getPage('/syllabus'),
      getProgramMetadata({ programKeys: [program.key, ...programPartKeys] }),
      getProgramContent(programKey),
      getProgramReviewSummary(programKey),
    ]);

    if (!page || !programMetadata || !programContent) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        programContentVersion: latestProgramVersion,
        page,
        program,
        programMetadata,
        programContent: programContent ?? ({} as ProgramContent),
        reviewSummary,
        relatedPrograms: getRelatedPrograms(programKey),
      },
      revalidate: 3600, // Revalidate every hour
    };
  } catch (error) {
    logger.error('An error occurred building Program page', {
      file: 'src/pages/syllabus/[slug].tsx',
      function: 'getStaticProps',
      programKey,
      error,
    });

    return {
      notFound: true,
    };
  }
}
